import AnimateHeight from 'react-animate-height';
import { trackProductSelectionChange } from '~/analytics/eventTracker';
import { ProductBundle, ProductWithBundleId } from '~/models/CampaignModel';
import { FlowMode } from '~/models/FlowMode';
import type { ProductType } from '~/models/product/ProductType';
import type { ProductTypeConfig } from '~/models/product/ProductTypeConfig';
import { SelectionMode } from '~/models/SelectionModel';
import { getFlowMode } from '../campaign.utils';
import { useBasket } from '../useBasket';
import { useMappedBundle } from '../useMappedBundles';
import { AdditionalComponents } from './product-group/components/bundle-additional-components/AdditionalComponents';
import { ProductBundleHeader } from './product-group/product-bundle/ProductBundleHeader';
import { ProductCard } from './product-group/product-cards/ProductCard';

export const ProductBundleComponent: React.FC<{
  group: ProductBundle<ProductWithBundleId>;
  config: ProductTypeConfig;
  type: ProductType;
  index: number;
  previousBundleId: string | undefined;
}> = ({ group, config, type, index, previousBundleId }) => {
  const selectProduct = useBasket((s) => s.selectProduct);
  const openBundle = useBasket((s) => s.openBundle);
  const showBundle = useBasket((s) => s.showBundle);
  const setActiveBundle = useBasket((s) => s.setActiveBundle);
  const activeBundleId = useBasket((s) => s.activeBundleId);
  const bundleVisibility = useBasket((s) => s.bundleVisibility)[group.id] ?? {};

  const { bundle, selectedInBundle, visibleItems } = useMappedBundle(group);
  const components = selectedInBundle.map((p) => p.displayData.additionalComponent).filter((a) => a != null);

  return (
    <div key={bundle.id} className="product-bundle">
      <ProductBundleHeader
        isOpen={bundleVisibility.isOpen}
        isVisible={bundleVisibility.isVisible}
        config={config}
        index={index}
        selectedInBundle={selectedInBundle}
        bundleId={bundle.id}
        activeBundleId={activeBundleId[type] ?? ''}
        deselectProduct={() => {
          const selectedBefore = useBasket.getState().selectedProducts;
          selectedInBundle.forEach((p) => selectProduct(p, bundle));
          trackProductSelectionChange(selectedBefore);
          openBundle(bundle.id, true);
        }}
        removeBundle={() => {
          const selectedBefore = useBasket.getState().selectedProducts;
          selectedInBundle.forEach((p) => selectProduct(p, bundle));
          trackProductSelectionChange(selectedBefore);
          if (previousBundleId) {
            setActiveBundle(type, previousBundleId);
          }
          openBundle(bundle.id, false);
          showBundle(bundle.id, false);
        }}
        flowMode={getFlowMode(type)}
      />

      {/* BUNDLE ITEMS */}
      <AnimateHeight duration={300} animateOpacity height={bundleVisibility.isOpen ? 'auto' : 0}>
        <ul style={{ display: 'flex', flexWrap: 'wrap', listStyleType: 'none', padding: 0 }}>
          {visibleItems.map((p) => (
            <ProductCard
              key={p.id}
              isOpen={true}
              isSelected={selectedInBundle.some((sp) => sp.id === p.id)}
              product={p}
              shouldFocus={false}
              toggleProductSelection={() => {
                const willAddToBundle = selectedInBundle.length === 0;
                const selectedBefore = useBasket.getState().selectedProducts;
                selectProduct(p, bundle);
                trackProductSelectionChange(selectedBefore);
                // close single selection bundle if in progressive mode
                if (
                  willAddToBundle &&
                  bundle.selectionConfig.selectionMode === SelectionMode.Single &&
                  getFlowMode(p.productType) === FlowMode.Progressive
                ) {
                  openBundle(bundle.id, false);
                }
              }}
            />
          ))}
        </ul>
      </AnimateHeight>
      {bundleVisibility.isOpen && <AdditionalComponents components={components} />}
    </div>
  );
};
